import type { AssetReservation, ExternalService, ExternalServiceItemStatus, ExternalServiceRules } from "../types/imobi";
import { periodsOverlap } from "./reservations";

export const defaultExternalServiceRules: ExternalServiceRules = {
  participantsCanReserveByDefault: true,
  selfEnrollmentByDefault: false,
  peerTransferByDefault: false,
  pickupPopEnabled: false,
  returnPopEnabled: true,
  returnPopPerformer: "responsavel",
  requirePickupConference: true
};

export function resolveExternalServiceRules(rules?: Partial<ExternalServiceRules> | null): ExternalServiceRules {
  const source = rules ?? {};
  return {
    participantsCanReserveByDefault: typeof source.participantsCanReserveByDefault === "boolean" ? source.participantsCanReserveByDefault : defaultExternalServiceRules.participantsCanReserveByDefault,
    selfEnrollmentByDefault: typeof source.selfEnrollmentByDefault === "boolean" ? source.selfEnrollmentByDefault : defaultExternalServiceRules.selfEnrollmentByDefault,
    peerTransferByDefault: typeof source.peerTransferByDefault === "boolean" ? source.peerTransferByDefault : defaultExternalServiceRules.peerTransferByDefault,
    pickupPopEnabled: typeof source.pickupPopEnabled === "boolean" ? source.pickupPopEnabled : defaultExternalServiceRules.pickupPopEnabled,
    returnPopEnabled: typeof source.returnPopEnabled === "boolean" ? source.returnPopEnabled : defaultExternalServiceRules.returnPopEnabled,
    returnPopPerformer: source.returnPopPerformer === "conferente" ? "conferente" : "responsavel",
    requirePickupConference: typeof source.requirePickupConference === "boolean" ? source.requirePickupConference : defaultExternalServiceRules.requirePickupConference
  };
}

const DAY_MS = 24 * 60 * 60 * 1000;

/** Datas "AAAA-MM-DD" viram meia-noite local; o resto segue o parse normal (ISO com horario). */
export function parseDateValue(value?: string | null): Date | null {
  if (!value) {
    return null;
  }

  const date = /^\d{4}-\d{2}-\d{2}$/.test(value) ? new Date(`${value}T00:00:00`) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

export function dateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

/** Quantidade de dias de calendario entre duas datas, contando o primeiro e o ultimo. */
export function inclusiveDays(start: Date, end: Date): number {
  const diff = Math.round((startOfDay(end).getTime() - startOfDay(start).getTime()) / DAY_MS);
  return Math.max(1, diff + 1);
}

export type ReservationPeriod = {
  start: Date;
  end: Date;
  openEnded: boolean;
};

/**
 * Periodo efetivo de uma reserva: usa as datas reais de saida/retorno quando existem
 * e cai para as previstas. Reserva sem previsao de retorno fica "em aberto": para exibir,
 * vai ate o fim da janela visivel (o conflito ja trata esse caso como infinito).
 */
export function reservationPeriod(reservation: AssetReservation, now: Date, openEndFallback?: Date): ReservationPeriod | null {
  const start = parseDateValue(reservation.actualStartAt) ?? parseDateValue(reservation.plannedStartAt);
  if (!start) {
    return null;
  }

  const actualEnd = parseDateValue(reservation.actualReturnAt);
  if (actualEnd) {
    return { start, end: actualEnd < start ? start : actualEnd, openEnded: false };
  }

  const expectedEnd = parseDateValue(reservation.expectedReturnAt);
  if (reservation.status === "em_uso") {
    // Ainda fora: ocupa ate hoje mesmo que a previsao ja tenha passado (atraso)
    const until = expectedEnd && expectedEnd > now ? expectedEnd : now;
    return { start, end: until < start ? start : until, openEnded: !expectedEnd };
  }

  if (reservation.status === "concluido") {
    return { start, end: expectedEnd && expectedEnd >= start ? expectedEnd : start, openEnded: false };
  }

  if (!expectedEnd) {
    const fallback = openEndFallback && openEndFallback > start ? openEndFallback : start;
    return { start, end: fallback, openEnded: true };
  }

  return { start, end: expectedEnd < start ? start : expectedEnd, openEnded: false };
}

export function isActiveReservation(reservation: AssetReservation): boolean {
  return reservation.status === "reservado" || reservation.status === "em_uso";
}

export type OccupancySegment = {
  reservation: AssetReservation;
  startIndex: number;
  endIndex: number;
  openEnded: boolean;
};

/**
 * Para cada bem, os trechos de reserva que caem na janela [rangeStart, rangeStart + days).
 * Indices sao dias a partir de rangeStart. Reservas canceladas ficam de fora.
 */
export function buildOccupancy(reservations: AssetReservation[], rangeStart: Date, days: number, now = new Date()): Map<string, OccupancySegment[]> {
  const windowStart = startOfDay(rangeStart);
  const windowEnd = addDays(windowStart, days - 1);
  const byAsset = new Map<string, OccupancySegment[]>();

  reservations.forEach((reservation) => {
    if (reservation.status === "cancelado") {
      return;
    }

    const period = reservationPeriod(reservation, now, windowEnd);
    if (!period) {
      return;
    }

    const start = startOfDay(period.start);
    const end = startOfDay(period.end);
    if (end < windowStart || start > windowEnd) {
      return;
    }

    const startIndex = Math.max(0, Math.round((start.getTime() - windowStart.getTime()) / DAY_MS));
    const endIndex = Math.min(days - 1, Math.round((end.getTime() - windowStart.getTime()) / DAY_MS));
    const segments = byAsset.get(reservation.assetId) ?? [];
    segments.push({ reservation, startIndex, endIndex, openEnded: period.openEnded });
    byAsset.set(reservation.assetId, segments);
  });

  byAsset.forEach((segments) => segments.sort((left, right) => left.startIndex - right.startIndex));
  return byAsset;
}

export type ServiceSpan = {
  service: ExternalService;
  start: Date;
  end: Date;
};

export function serviceSpan(service: ExternalService, now = new Date()): ServiceSpan | null {
  const start = parseDateValue(service.startDate);
  if (!start) {
    return null;
  }

  const actualEnd = parseDateValue(service.actualEndDate);
  const expectedEnd = parseDateValue(service.expectedEndDate);
  let end = actualEnd ?? expectedEnd ?? start;
  if (!actualEnd && ["em_andamento", "em_retorno"].includes(service.status) && end < now) {
    // Servico atrasado continua ocupando o calendario ate ser concluido
    end = now;
  }

  return { service, start: startOfDay(start), end: startOfDay(end < start ? start : end) };
}

export type AssetUsageMetrics = {
  assetId: string;
  servicesCount: number;
  totalDaysOut: number;
  averageDaysPerService: number;
  daysOutInWindow: number;
  utilizationInWindow: number;
  upcomingReservations: number;
  lastUseEnd?: Date;
  currentlyOut: boolean;
};

/**
 * Uso dos equipamentos a partir das reservas. Conta como uso real o que saiu
 * (em_uso ou concluido); reservas futuras entram so como "proximas".
 * A taxa de ocupacao considera os ultimos `windowDays` dias.
 */
export function computeAssetUsage(reservations: AssetReservation[], now = new Date(), windowDays = 90): Map<string, AssetUsageMetrics> {
  const today = startOfDay(now);
  const windowStart = addDays(today, -(windowDays - 1));
  const metrics = new Map<string, AssetUsageMetrics>();
  // Dias ja contados por bem na janela, para nao somar duas vezes servicos sobrepostos
  const windowDaysByAsset = new Map<string, Set<string>>();

  reservations.forEach((reservation) => {
    if (reservation.status === "cancelado") {
      return;
    }

    const current = metrics.get(reservation.assetId) ?? {
      assetId: reservation.assetId,
      servicesCount: 0,
      totalDaysOut: 0,
      averageDaysPerService: 0,
      daysOutInWindow: 0,
      utilizationInWindow: 0,
      upcomingReservations: 0,
      currentlyOut: false
    };

    if (reservation.status === "reservado") {
      const start = parseDateValue(reservation.plannedStartAt);
      if (start && startOfDay(start) >= today) {
        current.upcomingReservations += 1;
      }
      metrics.set(reservation.assetId, current);
      return;
    }

    const period = reservationPeriod(reservation, now);
    if (!period) {
      return;
    }

    current.servicesCount += 1;
    current.totalDaysOut += inclusiveDays(period.start, period.end);
    current.currentlyOut = current.currentlyOut || reservation.status === "em_uso";
    if (!current.lastUseEnd || period.end > current.lastUseEnd) {
      current.lastUseEnd = period.end;
    }

    const counted = windowDaysByAsset.get(reservation.assetId) ?? new Set<string>();
    let cursor = startOfDay(period.start < windowStart ? windowStart : period.start);
    const last = startOfDay(period.end > today ? today : period.end);
    while (cursor <= last) {
      counted.add(dateKey(cursor));
      cursor = addDays(cursor, 1);
    }
    windowDaysByAsset.set(reservation.assetId, counted);
    metrics.set(reservation.assetId, current);
  });

  metrics.forEach((entry) => {
    entry.averageDaysPerService = entry.servicesCount ? entry.totalDaysOut / entry.servicesCount : 0;
    entry.daysOutInWindow = windowDaysByAsset.get(entry.assetId)?.size ?? 0;
    entry.utilizationInWindow = entry.daysOutInWindow / windowDays;
  });

  return metrics;
}

const SERVICE_COLORS = ["#0f766e", "#2563eb", "#7c3aed", "#c2410c", "#be123c", "#0369a1", "#4d7c0f", "#a16207", "#9333ea", "#0e7490"];

/** Cor fixa por servico (mesma cor no calendario, na ocupacao e na legenda). */
export function serviceColor(serviceId: string): string {
  let hash = 0;
  for (let index = 0; index < serviceId.length; index += 1) {
    hash = (hash * 31 + serviceId.charCodeAt(index)) >>> 0;
  }
  return SERVICE_COLORS[hash % SERVICE_COLORS.length];
}

/* Rotulos e formatacao usados pelas telas de servicos externos. */

export function formatExternalServiceStatus(status: ExternalService["status"]) {
  const labels: Record<ExternalService["status"], string> = {
    rascunho: "Rascunho",
    aberto: "Planejado",
    aguardando_confirmacao: "Aguardando retirada",
    em_andamento: "Em campo",
    em_retorno: "Em retorno",
    concluido: "Concluído",
    cancelado: "Cancelado"
  };
  return labels[status] ?? status;
}

export function externalServiceStatusTone(status: ExternalService["status"]): "green" | "yellow" | "red" | "blue" | "neutral" {
  if (status === "concluido") return "green";
  if (status === "cancelado" || status === "rascunho") return "neutral";
  if (status === "em_retorno" || status === "aguardando_confirmacao") return "yellow";
  return "blue";
}

export function formatExternalServiceItemStatus(status: ExternalServiceItemStatus) {
  const labels: Record<ExternalServiceItemStatus, string> = {
    pendente_confirmacao: "Aguardando retirada",
    confirmado_com_responsavel: "Aguardando liberação",
    recusado: "Recusado",
    em_uso: "Em uso",
    retorno_pendente: "Retorno pendente",
    retorno_confirmado: "Aguardando conferência",
    devolvido: "Devolvido",
    devolvido_com_divergencia: "Com divergência",
    nao_devolvido: "Não devolvido",
    extraviado: "Extraviado"
  };
  return labels[status] ?? status;
}

export function externalServiceItemTone(status: ExternalServiceItemStatus): "green" | "yellow" | "red" | "blue" | "neutral" {
  if (status === "devolvido") return "green";
  if (status === "pendente_confirmacao" || status === "retorno_pendente" || status === "retorno_confirmado") return "yellow";
  if (status === "devolvido_com_divergencia" || status === "nao_devolvido" || status === "extraviado") return "red";
  if (status === "em_uso" || status === "confirmado_com_responsavel") return "blue";
  return "neutral";
}

const periodShortDate = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });
const periodFullDate = new Intl.DateTimeFormat("pt-BR", { dateStyle: "short" });

export function formatServicePeriod(start?: string, end?: string) {
  const startDate = parseDateValue(start);
  if (!startDate) {
    return "-";
  }

  const endDate = parseDateValue(end);
  if (!endDate) {
    return `${periodFullDate.format(startDate)} · sem previsão de fim`;
  }

  const days = inclusiveDays(startDate, endDate);
  const sameYear = startDate.getFullYear() === endDate.getFullYear();
  return `${sameYear ? periodShortDate.format(startDate) : periodFullDate.format(startDate)} → ${periodFullDate.format(endDate)} · ${days} dia${days > 1 ? "s" : ""}`;
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) {
    return "?";
  }

  return `${parts[0][0] ?? ""}${parts.length > 1 ? parts[parts.length - 1][0] ?? "" : ""}`.toUpperCase();
}

/** Conflito de um bem com o periodo de um servico, olhando as reservas da empresa. */
export function findAssetConflict(reservations: AssetReservation[], assetId: string, service: ExternalService): AssetReservation | undefined {
  return reservations
    .filter((reservation) => reservation.assetId === assetId && isActiveReservation(reservation) && reservation.serviceId !== service.id)
    .find((reservation) => periodsOverlap(service.startDate, service.expectedEndDate, reservation.plannedStartAt, reservation.expectedReturnAt));
}
